import TextAlign from "@tiptap/extension-text-align";

export const TEXT_ALIGN_CLASS_PREFIX = "text-align-";

export const TEXT_ALIGNMENTS = ["left", "center", "right"] as const;

export type TextAlignment = (typeof TEXT_ALIGNMENTS)[number];

const isTextAlignment = (value: string): value is TextAlignment =>
  (TEXT_ALIGNMENTS as readonly string[]).includes(value);

const parseAlignment = (element: HTMLElement): TextAlignment | null => {
  const fromClass = Array.from(element.classList)
    .filter((className) => className.startsWith(TEXT_ALIGN_CLASS_PREFIX))
    .map((className) => className.slice(TEXT_ALIGN_CLASS_PREFIX.length))
    .find(isTextAlignment);
  if (fromClass) {
    return fromClass;
  }

  const fromStyle = element.style.textAlign;
  return isTextAlignment(fromStyle) ? fromStyle : null;
};

/**
 * TextAlign serialized as a `text-align-*` class instead of an inline style,
 * since the backend sanitizer strips `style` attributes.
 */
export const ClassTextAlign = TextAlign.extend({
  // Alignment has no shortcut by design; the default ⌘⇧R also collides with the browser's hard reload.
  addKeyboardShortcuts() {
    return {};
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textAlign: {
            default: null,
            parseHTML: parseAlignment,
            renderHTML: (attributes) => {
              const alignment = attributes.textAlign;
              if (!alignment || !isTextAlignment(alignment)) {
                return {};
              }
              return { class: `${TEXT_ALIGN_CLASS_PREFIX}${alignment}` };
            },
          },
        },
      },
    ];
  },
}).configure({
  types: ["heading", "paragraph"],
  alignments: [...TEXT_ALIGNMENTS],
});
